/**
 * Converts a color in HEX format to an RGB object
 * @param {string} hex A color in HEX format
 * @returns {Object} { r, g, b }
 * @example hex2rgb('#ff0000'); // returns { r: 255, g: 0: b: 0 }
 * @example hex2rgb('#fff'); // returns { r: 255, g: 255, b: 255 }
 */
const hex2rgb = hex => {
  if (hex.length === 4) {
    let r = hex.slice(1, 2);
    let g = hex.slice(2, 3);
    let b = hex.slice(3, 4);
    r = parseInt(r + r, 16);
    g = parseInt(g + g, 16);
    b = parseInt(b + b, 16);
    return { r, g, b };
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

/**
 * Determines if a color is light or dark
 * Source: https://awik.io/determine-color-bright-dark-using-javascript/
 * @param {String} color A color in HEX or RGB format
 * @returns {string} `light` or `dark`
 * @example lightOrDark('rgb(0,0,0)') // returns 'dark'
 * @example lightOrDark('#fff') // returns 'light'
 */
const lightOrDark = color => {
  let r;
  let g;
  let b;
  // Check the format of the color, HEX or RGB?
  if (color.match(/^rgb/)) {
    // If RGB --> store the red, green, blue values in separate variables
    [r, g, b] = color.match(
      /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
    );
  } else {
    // If HEX --> Convert it to RGB
    const rgbColor = hex2rgb(color);
    ({ r, g, b } = rgbColor);
  }
  // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
  if (hsp > 127.5) {
    return 'light';
  }
  return 'dark';
};

/**
 * Generate shadows.
 * Source: https://github.com/mui/material-ui/blob/master/packages/mui-material/src/styles/shadows.js
 * @param  {number[]} px Array of 12 pixel values
 * @returns {string}
 */
const createShadow = (...px) => {
  const shadowKeyUmbraOpacity = 0.2;
  const shadowKeyPenumbraOpacity = 0.14;
  const shadowAmbientShadowOpacity = 0.12;
  return [
    `${px[0]}px ${px[1]}px ${px[2]}px ${
      px[3]
    }px rgba(0,0,0,${shadowKeyUmbraOpacity})`,
    `${px[4]}px ${px[5]}px ${px[6]}px ${
      px[7]
    }px rgba(0,0,0,${shadowKeyPenumbraOpacity})`,
    `${px[8]}px ${px[9]}px ${px[10]}px ${
      px[11]
    }px rgba(0,0,0,${shadowAmbientShadowOpacity})`,
  ].join(',');
};

/**
 * Generate transition css.
 * Source: https://github.com/mui/material-ui/blob/master/packages/mui-material/src/styles/createTransitions.js
 * @param {string[]} props Attributes to be animated
 * @param {Object} options Transition options
 * @param {string|number} options.duration The duration of the transition. Defaults to 300
 * @param {string} options.timingFunction The transition timing functions. Defaults to 'cubic-bezier(0.4, 0, 0.2, 1)'
 * @param {string|number} options.delay The transition delay. Defaults to 0
 * @returns
 */
const createTransition = (attributes = ['all'], options = {}) => {
  const {
    duration = 300,
    timingFunction = 'cubic-bezier(0.4, 0, 0.2, 1)',
    delay = 0,
  } = options;
  return (Array.isArray(attributes) ? attributes : [attributes])
    .map(
      animatedProp =>
        `${animatedProp} ${
          typeof duration === 'string' ? duration : `${Math.round(duration)}ms`
        } ${timingFunction} ${
          typeof delay === 'string' ? delay : `${Math.round(delay)}ms`
        }`
    )
    .join(',');
};

export { createShadow, createTransition, hex2rgb, lightOrDark };
